import { Box, Button, useTheme } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";

import { ErrorAlert, SuccessAlert } from "@/modules/alert";
import FormInput from "@/modules/common/components/FormInput";
import { Typography } from "@/modules/common/components/Typography";
import { useForm } from "@/modules/common/hooks/useForm";
import { useTranslation } from "@/modules/translations/useTranslation";
import { sendMagicLink } from "@/modules/users/services";
import { handleApiError } from "@/utils/request";

interface MagicForm {
  email: string;
}

function MagicLink() {
  const { t } = useTranslation();
  const { control, getValues, handleSubmit } = useForm<MagicForm, any>({
    defaultValues: {
      email: "",
    },
  });
  const mutation = useMutation<
    Response,
    AxiosError<{ message: string }>,
    { email: string }
  >(sendMagicLink);
  const theme = useTheme();

  if (mutation.isLoading) {
    return (
      <Typography color={theme.palette.grey[100]}>Envoi du mail...</Typography>
    );
  }

  if (mutation.isSuccess) {
    return (
      <>
        <SuccessAlert />
        <div className="flex flex-col gap-4 text-white">
          {t("page.login.magic-link-sent", {
            userEmail: getValues("email"),
            returnObjects: true,
          }).map((html) => (
            <Typography
              key={html}
              dangerouslySetInnerHTML={{ __html: html }}
            ></Typography>
          ))}
        </div>
      </>
    );
  }

  const onSubmit = ({ email }: MagicForm) => mutation.mutate({ email });

  return (
    <>
      {mutation.isError && (
        <ErrorAlert
          message={handleApiError(mutation.error, {
            USER_DOES_NOT_EXIST: () =>
              t("message.error.signup.USER_DOES_NOT_EXIST"),
            default: () => t("message.error.global.UNEXPECTED"),
          })}
        ></ErrorAlert>
      )}

      <Box display="flex" flexDirection="column" color="#ffffff">
        <Box mb={2}>
          <Typography variant="h4" mb={1}>
            {t("page.login.title")}
          </Typography>
          <Typography>{t("page.login.description")}</Typography>
        </Box>

        <Box mb={4}>
          <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
            <FormInput
              control={control}
              name="email"
              label={t("form.field.email.label")}
            />
            <Button color="secondary" variant="contained" type="submit">
              {t("cta.send-magic-link")}
            </Button>
          </form>
        </Box>

        <Box mb={2} textAlign="center">
          <Typography>{t("page.login.signup-section.title")}</Typography>
          <Typography>
            <Link className="hover:underline" to="/signup">
              {t("page.login.signup-section.cta")}
            </Link>
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default MagicLink;
